import React, { useState } from 'react';
import { InquiryHero, SuccessBanner, PrivacyNote, FormActions, submitInquiry } from './formUtils';
import { Section, TextField, TextareaField, SelectField, MultiSelectField, FileUploadField, ConditionalText, ConsentSection } from './FormFields';
import { CRDMO_OPTIONS, HOW_HEARD_OPTIONS, COUNTRY_LIST } from '../../data/partnerOptions';

const initial = {
  fullName: '', company: '', jobTitle: '', email: '', phone: '',
  countryIncorporation: '', countrySubsidiaries: [],
  industrySector: '', industrySectorOther: '', targetCompound: '', structureAvailable: '', projectStage: '',
  requiredGrade: '', requiredGradeCustom: '',
  targetQuantity: '', purityRequirement: '', analyticalNeeds: [], ipConfidentiality: '',
  timeline: '', additionalDetails: '', howHeard: '',
  consentRequired: false, consentOptional: false,
};

export const CRDMOForm = () => {
  const [data, setData] = useState(initial);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showConsentError, setShowConsentError] = useState(false);

  const set = (k) => (v) => setData((d) => ({ ...d, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!data.consentRequired) {
      setShowConsentError(true);
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const sections = [
        { title: 'Contact Information', fields: [
          { label: 'Full name', value: data.fullName },
          { label: 'Company / organization', value: data.company },
          { label: 'Job title / role', value: data.jobTitle },
          { label: 'Business email', value: data.email },
          { label: 'Phone number', value: data.phone },
          { label: 'Country of incorporation (Headquarters)', value: data.countryIncorporation },
        ]},
        { title: 'Project Overview', fields: [
          { label: 'Industry sector', value: data.industrySector },
          { label: 'Industry sector - specified', value: data.industrySectorOther },
          { label: 'Target compound / CAS number', value: data.targetCompound },
          { label: 'Chemical structure available?', value: data.structureAvailable },
          { label: 'Project stage', value: data.projectStage },
        ]},
        { title: 'Technical Specifications', fields: [
          { label: 'Required grade', value: data.requiredGrade },
          { label: 'Custom grade specification', value: data.requiredGradeCustom },
          { label: 'Target quantity (initial)', value: data.targetQuantity },
          { label: 'Purity requirement', value: data.purityRequirement },
          { label: 'Analytical / characterization needs', value: data.analyticalNeeds },
          { label: 'IP & confidentiality', value: data.ipConfidentiality },
        ]},
        { title: 'Timeline & Additional Context', fields: [
          { label: 'Desired timeline', value: data.timeline },
          { label: 'Additional project details', value: data.additionalDetails },
        ]},
        { title: 'Referral', fields: [
          { label: 'How did you hear about LAMKO?', value: data.howHeard },
        ]},
        { title: 'Consent & Data Protection', fields: [
          { label: 'Required data-processing consent', value: data.consentRequired ? 'Yes - consent given' : 'No' },
          { label: 'Optional marketing consent', value: data.consentOptional ? 'Yes' : 'No' },
        ]},
      ];
      await submitInquiry({
        formType: 'crdmo',
        sections,
        meta: { fullName: data.fullName, companyName: data.company, businessEmail: data.email, country: '' },
        files,
      });
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.message || 'Submission failed. Please try again.');
    }
    setSubmitting(false);
  };

  const reset = () => {
    setData(initial); setFiles([]); setError(''); setSuccess(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <InquiryHero
        title="Contract Research (CRDMO)"
        subtitle="Feasibility studies, route scouting & early-stage R&D"
      />
      <section className="py-10 md:py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-10">
            {success && <SuccessBanner />}
            {error && <div className="mb-6 bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm" data-testid="error-banner">{error}</div>}
            <form onSubmit={handleSubmit} className="space-y-8">
              <Section title="Contact Information">
                <div className="grid sm:grid-cols-2 gap-5">
                  <TextField name="fullName" label="Full name" required helper="Your first and last name as you'd like to be addressed." placeholder="e.g., Dr. Min-Jun Park" value={data.fullName} onChange={set('fullName')} />
                  <TextField name="company" label="Company / organization" required helper="Legal entity or institution name." placeholder="e.g., ABC Co., Ltd." value={data.company} onChange={set('company')} />
                  <TextField name="jobTitle" label="Job title / role" required helper="Your current position or functional role." placeholder="e.g., Senior Research Scientist" value={data.jobTitle} onChange={set('jobTitle')} />
                  <TextField name="email" type="email" label="Business email" required helper="We'll use this email for all project correspondence." placeholder="e.g., mjpark@company.com" value={data.email} onChange={set('email')} />
                  <TextField name="phone" type="tel" label="Phone number" helper="Include country code. Used only for urgent follow-ups." placeholder="e.g., +82-10-1234-5678" value={data.phone} onChange={set('phone')} />
                </div>
                <SelectField name="countryIncorporation" label="Country of incorporation (Headquarters)" required helper="Where is your company legally registered?" options={COUNTRY_LIST} value={data.countryIncorporation} onChange={set('countryIncorporation')} />
              </Section>

              <Section title="Project Overview">
                <SelectField name="industrySector" label="Industry sector" required helper="Select the primary application domain for this research." options={CRDMO_OPTIONS.industrySector} value={data.industrySector} onChange={set('industrySector')} />
                <ConditionalText show={data.industrySector === 'Other (please specify)'} name="industrySectorOther" placeholder="Please describe your industry sector" value={data.industrySectorOther} onChange={set('industrySectorOther')} />
                <TextField name="targetCompound" label="Target compound / CAS number" required helper="Provide the chemical name, IUPAC name, or CAS registry number of the target molecule." placeholder="e.g., 4,4'-Bis(N-carbazolyl)-1,1'-biphenyl (CBP) / CAS 58328-31-7" value={data.targetCompound} onChange={set('targetCompound')} />
                <SelectField name="structureAvailable" label="Chemical structure available?" required helper="Let us know if you can share structural data for preliminary evaluation." options={CRDMO_OPTIONS.structureAvailable} value={data.structureAvailable} onChange={set('structureAvailable')} />
                <SelectField name="projectStage" label="Project stage" required helper="Current maturity level of the compound or project." options={CRDMO_OPTIONS.projectStage} value={data.projectStage} onChange={set('projectStage')} />
              </Section>

              <Section title="Technical Specifications">
                <SelectField name="requiredGrade" label="Required grade" required helper="Select the product grade that matches your application requirements." options={CRDMO_OPTIONS.requiredGrade} value={data.requiredGrade} onChange={set('requiredGrade')} />
                <ConditionalText show={data.requiredGrade === 'Custom grade (please specify below)'} name="requiredGradeCustom" label="Custom grade specification" helper="Please describe your custom grade requirements." placeholder="e.g., Sub-ppm metal impurity grade for device-level evaluation" value={data.requiredGradeCustom} onChange={set('requiredGradeCustom')} />
                <SelectField name="targetQuantity" label="Target quantity (initial)" required helper="Approximate amount needed for initial research phase." options={CRDMO_OPTIONS.targetQuantity} value={data.targetQuantity} onChange={set('targetQuantity')} />
                <SelectField name="purityRequirement" label="Purity requirement" helper="Minimum acceptable purity for your application." options={CRDMO_OPTIONS.purityRequirement} value={data.purityRequirement} onChange={set('purityRequirement')} />
                <MultiSelectField name="analyticalNeeds" label="Analytical / characterization needs" helper="Select all analytical services you require." options={CRDMO_OPTIONS.analyticalNeeds} values={data.analyticalNeeds} onChange={set('analyticalNeeds')} columns={2} />
                <SelectField name="ipConfidentiality" label="IP & confidentiality" helper="How should intellectual property be managed?" options={CRDMO_OPTIONS.ipConfidentiality} value={data.ipConfidentiality} onChange={set('ipConfidentiality')} />
              </Section>

              <Section title="Timeline & Additional Context">
                <SelectField name="timeline" label="Desired timeline" helper="When do you need initial results or deliverables?" options={CRDMO_OPTIONS.timeline} value={data.timeline} onChange={set('timeline')} />
                <TextareaField name="additionalDetails" label="Additional project details" helper="Provide any supplementary context: literature references, target applications, known challenges, preferred synthetic approaches, etc." placeholder="e.g., We are developing a next-generation blue OLED emitter with improved color purity. Key challenge is achieving high quantum yield while maintaining thermal stability above 300°C..." value={data.additionalDetails} onChange={set('additionalDetails')} />
              </Section>

              <Section title="Document Upload">
                <FileUploadField name="files" label="Upload technical documents" helper="Accepted formats: PDF, DOC, DOCX, XLS, XLSX, CDX, MOL, SDF (max 25 MB per file). You may upload chemical structures, project briefs, SDS, target specifications, or literature references." files={files} onChange={setFiles} />
                <SelectField name="howHeard" label="How did you hear about LAMKO?" helper="This helps us understand our reach." options={HOW_HEARD_OPTIONS} value={data.howHeard} onChange={set('howHeard')} />
              </Section>

              <FormActions onReset={reset} isSubmitting={submitting} />
              <ConsentSection variant="customer" consent1={data.consentRequired} consent2={data.consentOptional} onChange1={set('consentRequired')} onChange2={set('consentOptional')} showError={showConsentError} />
              <PrivacyNote />
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};
